/**
 * DeletePatientModal Component
 *
 * Type-to-confirm modal for patient profile deletion.
 * User must type "PERMANENTLY DELETE" to enable the confirm button.
 */

import { useState, useEffect, useCallback } from 'react';
import { X, AlertTriangle } from 'lucide-react';

const CONFIRM_PHRASE = 'PERMANENTLY DELETE';

interface DeletePatientModalProps {
  isOpen: boolean;
  patientName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function DeletePatientModal({
  isOpen,
  patientName,
  onConfirm,
  onCancel,
  isLoading = false,
}: DeletePatientModalProps) {
  const [confirmText, setConfirmText] = useState('');

  const isConfirmEnabled = confirmText === CONFIRM_PHRASE;

  // Reset text when modal opens/closes
  useEffect(() => {
    if (!isOpen) setConfirmText('');
  }, [isOpen]);

  // Handle ESC key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onCancel();
      }
    },
    [onCancel, isLoading]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={isLoading ? undefined : onCancel}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-patient-modal-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
              </div>
              <h2
                id="delete-patient-modal-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                Delete Patient Profile
              </h2>
            </div>
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded transition-colors duration-150 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              You are about to delete the patient profile for{' '}
              <span className="font-semibold text-gray-900 dark:text-gray-100">{patientName}</span>.
              This will remove the patient from the database and hide all associated
              documents, history logs, and reviews.
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 font-medium">
              This action cannot be undone from the UI.
            </p>
            <div>
              <label
                htmlFor="delete-confirm-input"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Type <span className="font-mono font-bold text-red-600 dark:text-red-400">{CONFIRM_PHRASE}</span> to confirm
              </label>
              <input
                id="delete-confirm-input"
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                disabled={isLoading}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] sm:min-h-0"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={!isConfirmEnabled || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] sm:min-h-0"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Deleting...
                </span>
              ) : (
                'Delete Patient'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
